import React, { useEffect, useRef } from 'react';
import { FilterDataType } from '@recone/models';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import Tooltip from '@mui/material/Tooltip';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import '../FilterStyles.scss';

const useChipStyles = makeStyles({
  chipRoot: {
    maxWidth: '200px',
    '&.MuiChip-colorError .ExclusionIcon': {
      background: '#d32f2f!important',
      boxShadow: '-3px 0px 0px 0rem #d32f2f!important'
    },
    '&:hover': {
      '& .MuiChip-label': {
        opacity: 1
      },
      '& .ExclusionIcon': {
        display: 'flex',
        background: 'rgb(29 119 75)',
        marginLeft: '-23px',
        zIndex: 1000,
        boxShadow: '-3px 0px 0px 0rem rgb(29 119 75)'
      },
      '& .DeleteIcon ': {
        order: 2,
        color: 'white' // since this is greater than an order of 1, it goes after the icon
      }
    },
    '& .ExclusionIcon': {
      order: 1, // the label has a default order of 0, so this icon goes after the label
      marginRight: '5px', // add some space between icon and delete icon
      cursor: 'pointer',
      display: 'none'
    },
    '& .DeleteIcon ': {
      order: 2, // since this is greater than an order of 1, it goes after the icon
      color: 'white'
    }
  }
});

interface Props {
  filter: FilterDataType;
  onFilterChange(value: FilterDataType): void;
}

const CreateableMultiFilterNoContext = ({ filter, onFilterChange }: Props) => {
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const chipClasses = useChipStyles();
  const onPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste: string = e.clipboardData.getData('text');
    const items = paste
      .split(/[\r\n;\t]+/)
      .map((item) => item.trim())
      .filter((item) => item.length);

    onFilterChange({ ...filter, criteria: items.map((item) => ({ value: item, included: true })) });
  };

  return (
    <>
      <Autocomplete
        size="small"
        onPaste={onPaste}
        onChange={(_e, _v, r, details) => {
          if (r === 'clear') {
            onFilterChange({ ...filter, criteria: [] });
            return;
          } else if (r === 'createOption') {
            if (!details) {
              return;
            }

            if (filter.criteria.some((x) => x.value == details.option as unknown as string)) {
              return;
            }
            onFilterChange({
              ...filter,
              criteria: [...filter.criteria, { value: details.option as unknown as string, included: true }]
            });
          } else if (r === 'removeOption') {
            onFilterChange({
              ...filter,
              criteria: [...filter.criteria.filter((x) => x.value != (details?.option as unknown as string))]
            });
          }
        }}
        multiple
        options={[]}
        defaultValue={[]}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={filter.criteria}
        getOptionLabel={(option) => option as string}
        filterSelectedOptions
        freeSolo
        renderTags={(value: { value: string; included: boolean }[]) => (
          <Box p={1} display="flex" flexWrap="wrap" gap={0.4}>
            {value.map((option: { value: string; included: boolean }) => (
              <Tooltip arrow placement="top" title={option.value} key={option.value}>
                <Chip
                  classes={{ root: chipClasses.chipRoot }}
                  size="small"
                  label={option.value}
                  onDelete={
                    () =>
                      onFilterChange({
                        ...filter,
                        criteria: filter.criteria.filter((x) => x.value != option.value)
                      }) 
                  }
                  deleteIcon={<CloseIcon className="DeleteIcon" />}
                  icon={
                    option.included ? (
                      <RemoveCircleOutlineIcon className="ExclusionIcon" />
                    ) : (
                      <AddCircleOutlineIcon className="ExclusionIcon" />
                    )
                  }
                  color={option.included ? 'success' : 'error'}
                  sx={{ color: 'white' }}
                  onClick={() => onFilterChange({ ...filter, criteria: filter.criteria.map(x => x.value == option.value ? { ...x, included: !x.included } : x) })}
                />
              </Tooltip>
            ))}
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label={filter.name} size="small" placeholder={filter.name} />}
      />
    </>
  );
};

export default CreateableMultiFilterNoContext;
